import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex } from '@chakra-ui/react';
import { hasValue, useT } from '@progress-fe/core';
import { arrayMove } from '@dnd-kit/sortable';
import { v4 as uuidv4 } from 'uuid';
import { Dialog } from '../../base';
import { SvgMenu } from '../../menu';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldError, JsFieldInspector } from '../../jsFormBase';
import { useDialog, useJsFieldFormData } from '../../../hooks';
import { PipelineOverview, PipelineConfigurator } from './components';
import { getSegmentMenuItems } from './PipelineConfigJsField.helpers';
/**
 * Component renders "/schemas/jsf-pipeline-config" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is TFormData.
 */
export const PipelineConfigJsField = (props) => {
    const { isInspector } = useContext(JsFormConfigContext) ?? {};
    const [isDialog, setIsDialog] = useState(false);
    const menuRef = useRef(null);
    const { t } = useT();
    const DeleteDialog = useDialog({ title: t('jsfields.PipelineConfig.deletingSegment') });
    const formData = useMemo(() => (hasValue(props.formData) ? props.formData : null), [props.formData]);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: formData?.fieldConfig ?? null,
        values: formData?.fieldValues ?? null,
        watchPropsChanges: true,
        onChange: props.onChange
    });
    useEffect(() => {
        onChangeFieldConfig(formData?.fieldConfig);
    }, [formData?.fieldConfig, onChangeFieldConfig]);
    const handleToggleDialog = () => {
        setIsDialog(!isDialog);
    };
    const handleSegmentDragEnd = (event) => {
        const from = event.active.data.current?.sortable.index;
        const to = event.over?.data.current?.sortable.index;
        if (fieldValues && hasValue(from) && hasValue(to) && from !== to) {
            const segmentsOrder = arrayMove(fieldValues.segmentsOrder, from, to);
            onChangeFieldValues({ ...fieldValues, segmentsOrder });
        }
    };
    const handleSegmentValueChange = (uuid, groupKey, key, value) => {
        if (fieldValues && fieldConfig) {
            const fieldValuesCopy = structuredClone(fieldValues);
            // Type of segments is changing
            if (groupKey === 'basic' && key === 'segment_type') {
                const sourceSegmentName = fieldValues.segments[uuid]['basic']['segment_name'].value;
                const segmentWithDefaultValues = fieldConfig.segmentDefinitions[value];
                segmentWithDefaultValues['basic']['segment_name'].value = sourceSegmentName;
                fieldValuesCopy.segments[uuid] = segmentWithDefaultValues;
                onChangeFieldValues(fieldValuesCopy);
            }
            // Other fields
            else {
                fieldValuesCopy.segments[uuid][groupKey][key].value = value;
                onChangeFieldValues(fieldValuesCopy);
            }
        }
    };
    const handleSegmentAdd = (type) => {
        const segmentDefinition = fieldConfig?.segmentDefinitions[type];
        if (fieldValues && segmentDefinition) {
            const uuid = uuidv4();
            onChangeFieldValues({
                ...fieldValues,
                segmentsOrder: [...fieldValues.segmentsOrder, uuid],
                segments: {
                    ...fieldValues.segments,
                    [uuid]: segmentDefinition
                }
            });
        }
    };
    const handleSegmentDelete = (uuid) => {
        if (fieldValues) {
            const name = fieldValues.segments[uuid]['basic']['segment_name'].value;
            DeleteDialog.open(t('jsfields.PipelineConfig.deleteSureMsg', { name: name }), {
                apply: {
                    title: t('actions.delete'),
                    isDanger: true,
                    onClick: async () => {
                        const entries = Object.entries(fieldValues.segments);
                        const newSegments = Object.fromEntries(entries.filter(([key]) => key !== uuid));
                        onChangeFieldValues({
                            ...fieldValues,
                            segmentsOrder: fieldValues.segmentsOrder.filter((s) => s !== uuid),
                            segments: newSegments
                        });
                    }
                },
                close: { title: t('actions.cancel') }
            });
        }
    };
    if (!fieldConfig || !fieldValues) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(Box, { pb: "2px", "data-testid": "PipelineConfigJsField-test", "data-name": props.name, children: [_jsx(DeleteDialog.Render, {}), _jsx(PipelineOverview, { fieldValues: fieldValues, isReadOnly: props.schema.readOnly, onDragEnd: handleSegmentDragEnd, onDelete: handleSegmentDelete }), _jsx(Box, { pt: '8px', children: _jsx(Button, { size: "xs", w: '100%', variant: "ghost", onClick: handleToggleDialog, children: t('jsfields.PipelineConfig.segmentConfig') }) }), isDialog && (_jsx(Dialog, { noOverflow: true, onClose: handleToggleDialog, title: _jsxs(Flex, { alignItems: 'center', gap: '12px', children: [_jsx(Box, { fontWeight: 700, children: t('jsfields.PipelineConfig.segmentConfig') }), !props.schema.readOnly && (_jsx(Box, { ref: menuRef, children: _jsx(SvgMenu, { as: "button", offset: [0, 4], containerRef: menuRef, variant: "compactGray", buttonVariant: "ghost", placement: "bottom-start", items: getSegmentMenuItems(), itemsChildrenMaxHeight: 460, buttonLabel: t('jsfields.PipelineConfig.addSegment'), onSelect: (value) => handleSegmentAdd(value) }) }))] }), children: _jsx(PipelineConfigurator, { fieldConfig: fieldConfig, fieldValues: fieldValues, isReadOnly: props.schema.readOnly, onValueChange: handleSegmentValueChange, onDragEnd: handleSegmentDragEnd, onDelete: handleSegmentDelete }) }))] }) }));
};
//# sourceMappingURL=PipelineConfigJsField.js.map